import { useQuery } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { CompanyResponseSchema } from "../response/IndexCompanyResponse";

/**
 * Mendefinisikan tipe data untuk objek filter.
 * Ini memungkinkan filter berdasarkan ID (bisa beberapa, dipisahkan koma) dan nama.
 */
interface CompanyFilterProps {
    id?: string;
    name?: string;
}

/**
 * Mendefinisikan semua properti yang bisa diterima oleh hook useIndexCompanies.
 * Ini mencakup semua parameter yang didukung oleh API endpoint.
 */
interface IndexCompaniesProps {
    filters?: CompanyFilterProps;
    include?: string; // contoh: "groups"
    search?: string;
    orderBy?: string;
    sort?: 'asc' | 'desc';
    limit?: number;
    page?: number;
    all?: boolean;
}

/**
 * Custom hook untuk mengambil daftar perusahaan dari API.
 * Menggunakan @tanstack/react-query untuk caching, re-fetching, dan state management.
 *
 * @param query - Objek yang berisi parameter untuk kustomisasi query API.
 * @returns Hasil dari useQuery, termasuk data, isLoading, isError, dll.
 */
const useIndexCompanies = (query: IndexCompaniesProps) => {
    return useQuery({
        // Kunci query dibuat dinamis berdasarkan parameter yang diberikan.
        // Ini memastikan bahwa React Query akan membuat cache terpisah untuk setiap
        // kombinasi filter yang unik dan akan me-refetch data saat parameter berubah.
        queryKey: ['companies', query],

        queryFn: async () => {
            try {
                // Membangun objek parameter untuk dikirim bersama request Axios.
                // Parameter API diformat sesuai dengan spesifikasi (misal: 'filter[id]').
                const params: Record<string, any> = {
                    'filter[id]': query.filters?.id,
                    'filter[name]': query.filters?.name,
                    include: query.include,
                    search: query.search,
                    orderBy: query.orderBy ?? 'created_at', // Default sorting
                    sort: query.sort ?? 'desc',             // Default sorting
                    limit: query.limit ?? 10,               // Default limit
                    page: query.page,
                    all: query.all ? 1 : undefined,
                };

                // Membersihkan parameter yang tidak memiliki nilai (undefined atau null)
                // agar tidak dikirim sebagai query string kosong ke API.
                Object.keys(params).forEach(key =>
                    (params[key] === undefined || params[key] === null) && delete params[key]
                );

                const response = await privateApi.get(`/v4/company`, { params });

                // Validasi respons dari API menggunakan skema Zod.
                const validationResult = CompanyResponseSchema.safeParse(response.data);
                if (!validationResult.success) {
                    console.error("API Response Validation Failed:", validationResult.error.errors);
                    // Melempar error jika data tidak sesuai dengan skema yang diharapkan.
                    throw new Error("Invalid data format received from the server.");
                }

                // Mengembalikan data yang sudah divalidasi dan terjamin tipenya.
                return validationResult.data;
            } catch (error) {
                console.error("Failed to fetch companies:", error);
                // Melempar kembali error agar bisa ditangani oleh React Query.
                throw error;
            }
        },
        // Konfigurasi tambahan untuk useQuery, sesuai dengan praktik terbaik.
        retry: 1, // Hanya coba lagi 1 kali jika request gagal.
        refetchOnWindowFocus: false, // Tidak me-refetch saat window kembali fokus.
    });
};

export default useIndexCompanies;